import React, { useState } from 'react';
import { Col, Row } from 'components/Core/Grid';
import { ToggleButton } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Core/Button';
import Select from 'components/Core/Select';
import Text from 'components/Core/Text';
import TextField from 'components/Core/TextField';
import FormLabelWithField from 'components/Core/FormLabelWithField';
import HyperLink from 'components/Core/HyperLink';
import EmbedLink from 'components/Tools/EmbedLink';
import EmbedForm from 'components/Tools/EmbeddableForm';
import { getFormValid } from 'utils/formUtils';
import {
  Calculator,
  Container,
  Results,
  FormInputContainer,
  UncommonValue,
  ToggleButtonContainer,
} from './styles';
import { HvacDuctCalculation } from './calculation';
import GatedSubscribeForm from '../GatedSubscribeForm';
import { STIcon } from '../../Brand/Icons/STIcon';

const DuctForm = ({ gatedForm, ...otherProps }) => {
  const [gateFormCookieIncrease, setGateFormCookieIncrease] = useState(0);
  const [uniqueSubmission, setUniqueSubmission] = useState(true);

  const formik = useFormik({
    initialValues: {
      ductType: 'metal', //
      ductLength: '',
      sizeBy: 'friction rate', //
      frictionRate: '',
      velocity: '',
      cfm: '',
      ductSizeOrCfm: 'ductSize', //
      shape: 'round', //
      diameter: '',
      length: '',
      width: '',
      f: 0, //# of fourty five degree angles
      n: 0, //# of ninety degree angles
      o: 0, //# of one hundred eighty degree angles
    },
    validationSchema: Yup.object().shape({
      ductLength: Yup.number().required().moreThan(0),
      frictionRate: Yup.number().when('sizeBy', {
        is: 'friction rate',
        then: Yup.number().required(),
      }),
      velocity: Yup.number().when('sizeBy', {
        is: 'velocity',
        then: Yup.number().required(),
      }),
      cfm: Yup.number().when('ductSizeOrCfm', {
        is: 'cfm',
        then: Yup.number().required(),
      }),
      diameter: Yup.number().when(['ductSizeOrCfm', 'shape'], {
        is: (ductSizeOrCfm, shape) => ductSizeOrCfm === 'ductSize' && shape === 'round',
        then: Yup.number().required(),
      }),
      length: Yup.number().when(['ductSizeOrCfm', 'shape'], {
        is: (ductSizeOrCfm, shape) => ductSizeOrCfm === 'ductSize' && shape === 'rectangular',
        then: Yup.number().required(),
      }),
      width: Yup.number().when(['ductSizeOrCfm', 'shape'], {
        is: (ductSizeOrCfm, shape) => ductSizeOrCfm === 'ductSize' && shape === 'rectangular',
        then: Yup.number().required(),
      }),
    }),
    onSubmit: (values) => {
      // eslint-disable-next-line no-use-before-define
      calculate(values);
    },
  });

  const [results, setResults] = useState({});

  const onHandleChange = (id, newValue) => {
    formik.setFieldTouched(id);
    formik.setFieldValue(id, newValue);
    setUniqueSubmission(true);
  };

  const calculate = (values) => {
    dataLayer.push({
      event: 'calculator',
      calculator_type: 'HVAC Duct Calculator',
      duct_type: values.ductType,
      duct_length: values.ductLength,
      ...(values.sizeBy === 'friction rate'
        ? { friction_rate: values.frictionRate }
        : { velocity: values.velocity }),
      ...(values.ductSizeOrCfm === 'cfm'
        ? { cfm: values.cfm }
        : values.shape === 'round'
        ? { diameter: values.diameter }
        : { length: values.length, width: values.width }),
      num_of_ninety_degree_bends: values.n,
      num_of_forty_five_degree_bends: values.f,
      num_of_one_hundred_eighty_degree_bends: values.o,
    });
    setGateFormCookieIncrease(gateFormCookieIncrease + 1);
    const result = HvacDuctCalculation(values);
    setResults(result);
    setUniqueSubmission(false);
  };

  const generateResultDescription = (r) => {
    const descriptions = {
      cfm: 'CFM',
      velocity: 'Velocity (FPM)',
      frictionRate: `Friction Loss per ${
        formik.values.ductType === 'metal' ? '100' : '25'
      }' of Duct`,
    };
    return `${descriptions[r] || 'Duct Diameter (inches)'} = `;
  };

  const formValid = getFormValid({ ...formik });

  return (
    <Container isEmbed={otherProps.isEmbed}>
      <Calculator isEmbed={otherProps.isEmbed} data-position="hvac-duct-calculator">
        <GatedSubscribeForm gatedForm={gatedForm} increaseCookie={gateFormCookieIncrease}>
          <Row gutter={[40, 30]}>
            <Col md={24} lg={24} className="duct-type-container">
              <FormLabelWithField label="Duct Type">
                <ToggleButtonContainer>
                  <ToggleButton
                    data-testid="ductType-metal"
                    selected={formik.values.ductType === 'metal'}
                    onClick={() => onHandleChange('ductType', 'metal')}
                    value="metal"
                  >
                    Metal
                  </ToggleButton>
                  <ToggleButton
                    data-testid="ductType-flex"
                    selected={formik.values.ductType === 'flex'}
                    onClick={() => onHandleChange('ductType', 'flex')}
                    value="flex"
                  >
                    Flex
                  </ToggleButton>
                </ToggleButtonContainer>
              </FormLabelWithField>
            </Col>
          </Row>
          <div>
            <Row gutter={[40, 30]}>
              <Col xs={24} md={12}>
                <FormLabelWithField label="Size by">
                  <Select
                    id="sizeBy"
                    required
                    values={formik.values}
                    defaultValue={formik.initialValues['sizeBy']}
                    options={[
                      { label: 'Friction rate', value: 'friction rate' },
                      { label: 'Velocity', value: 'velocity' },
                    ]}
                    handleChange={onHandleChange}
                  />
                </FormLabelWithField>
              </Col>
              {formik.values.sizeBy === 'velocity' ? (
                <Col xs={24} md={12}>
                  <FormLabelWithField isRequired label="Velocity">
                    <FormInputContainer>
                      <TextField
                        showOnlyFormError
                        id="velocity"
                        type="number"
                        values={formik.values}
                        formValid={formValid}
                        handleChange={onHandleChange}
                        allowNegative={false}
                      />
                      <Text size="18px" type="p">
                        fpm
                      </Text>
                    </FormInputContainer>
                  </FormLabelWithField>
                </Col>
              ) : (
                <Col xs={24} md={12}>
                  <FormLabelWithField
                    isRequired
                    label={
                      <>
                        Friction Loss rate{' '}
                        <HyperLink className="conversion-link" href="#friction-rate-info">
                          View Common Rates
                        </HyperLink>
                      </>
                    }
                  >
                    <FormInputContainer>
                      <TextField
                        showOnlyFormError
                        id="frictionRate"
                        type="number"
                        values={formik.values}
                        formValid={formValid}
                        handleChange={onHandleChange}
                        allowNegative={false}
                        sx={{ width: 'auto !important' }}
                      />
                      <Text size="16px" type="p">
                        per 100 ft (in/wg)
                      </Text>
                    </FormInputContainer>
                  </FormLabelWithField>
                  {formik.values.frictionRate !== '' &&
                    (formik.values.frictionRate < 0.1 || formik.values.frictionRate > 8) && (
                      <UncommonValue>
                        <Text size="13px" type="p">
                          <STIcon
                            type="warning_icon"
                            alt="Warning Icon"
                            style={{ margin: '0px 6px 0px 0px' }}
                          />
                          Uncommon Friction Rate Value
                        </Text>
                      </UncommonValue>
                    )}
                </Col>
              )}
            </Row>
            <Row gutter={[40, 30]}>
              <Col xs={24} sm={24} md={8} lg={8}>
                <FormLabelWithField label="# of 45° Bends">
                  <TextField
                    showOnlyFormError
                    id="f"
                    type="number"
                    values={formik.values}
                    formValid={formValid}
                    handleChange={onHandleChange}
                    allowNegative={false}
                  />
                </FormLabelWithField>
              </Col>
              <Col xs={24} sm={24} md={8} lg={8}>
                <FormLabelWithField label="# of 90° Bends">
                  <TextField
                    showOnlyFormError
                    id="n"
                    type="number"
                    values={formik.values}
                    formValid={formValid}
                    handleChange={onHandleChange}
                    allowNegative={false}
                  />
                </FormLabelWithField>
              </Col>
              <Col xs={24} sm={24} md={8} lg={8}>
                <FormLabelWithField label="# of 180° Bends">
                  <TextField
                    showOnlyFormError
                    id="o"
                    type="number"
                    values={formik.values}
                    formValid={formValid}
                    handleChange={onHandleChange}
                    allowNegative={false}
                  />
                </FormLabelWithField>
              </Col>
            </Row>
            <Row gutter={[40, 30]}>
              <Col xs={12} sm={12} md={12} lg={12}>
                <FormLabelWithField label="Duct TEL" isRequired>
                  <TextField
                    showOnlyFormError
                    id="ductLength"
                    type="number"
                    values={formik.values}
                    formValid={formValid}
                    handleChange={onHandleChange}
                    placeholder="feet"
                    allowNegative={false}
                  />
                </FormLabelWithField>
              </Col>
              <Col xs={12} sm={12} md={12} lg={12}>
                <Text size="16px" type="p" className="external-helper-text">
                  * include fittings in the Duct TEL
                </Text>
              </Col>
            </Row>
            <div>
              <Row gutter={[40, 30]}>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <FormLabelWithField label="Enter either">
                    <Select
                      id="ductSizeOrCfm"
                      required
                      values={formik.values}
                      options={[
                        { label: 'CFM', value: 'cfm' },
                        { label: 'Duct Size', value: 'ductSize' },
                      ]}
                      handleChange={onHandleChange}
                      defaultValue={formik.initialValues['ductSizeOrCfm']}
                    />
                  </FormLabelWithField>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12}>
                  {formik.values.ductSizeOrCfm === 'cfm' ? (
                    <FormLabelWithField label="CFM" isRequired>
                      <TextField
                        showOnlyFormError
                        id="cfm"
                        type="number"
                        values={formik.values}
                        formValid={formValid}
                        handleChange={onHandleChange}
                        allowNegative={false}
                      />
                    </FormLabelWithField>
                  ) : (
                    <>
                      <FormLabelWithField
                        label={
                          <>
                            Duct shape{' '}
                            <HyperLink className="conversion-link" href="#utility-functions">
                              Convert Here
                            </HyperLink>
                          </>
                        }
                      >
                        <Select
                          id="shape"
                          required
                          values={formik.values}
                          options={[
                            { label: 'Round', value: 'round' },
                            { label: 'Rectangular', value: 'rectangular' },
                          ]}
                          handleChange={onHandleChange}
                          defaultValue={formik.initialValues['shape']}
                        />
                      </FormLabelWithField>
                    </>
                  )}
                </Col>
              </Row>
            </div>
            {formik.values.ductSizeOrCfm === 'ductSize' && (
              <Row gutter={[40, 30]}>
                {formik.values.shape === 'round' ? (
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <FormLabelWithField label="Diameter" isRequired>
                      <TextField
                        showOnlyFormError
                        id="diameter"
                        type="number"
                        values={formik.values}
                        formValid={formValid}
                        handleChange={onHandleChange}
                        placeholder="inches"
                        allowNegative={false}
                      />
                    </FormLabelWithField>
                  </Col>
                ) : (
                  <>
                    <Col xs={12} sm={12} md={12} lg={12}>
                      <FormLabelWithField label="Side 1" isRequired>
                        <TextField
                          showOnlyFormError
                          id="length"
                          type="number"
                          values={formik.values}
                          formValid={formValid}
                          handleChange={onHandleChange}
                          placeholder="inches"
                          allowNegative={false}
                        />
                      </FormLabelWithField>
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={12}>
                      <FormLabelWithField label="Side 2" isRequired>
                        <TextField
                          showOnlyFormError
                          id="width"
                          type="number"
                          values={formik.values}
                          formValid={formValid}
                          handleChange={onHandleChange}
                          placeholder="inches"
                          allowNegative={false}
                        />
                      </FormLabelWithField>
                    </Col>
                  </>
                )}
              </Row>
            )}
          </div>
          <Button
            disabled={!uniqueSubmission}
            onClick={() => formik.submitForm()}
            className="calculate"
            id="calculate"
          >
            Calculate
          </Button>
          <EmbedLink
            show={!otherProps.isEmbed}
            openCopyEmbedLinkDialog={otherProps?.openCopyEmbedLinkDialog}
            classNames="hvac-duct--iframe"
          />
        </GatedSubscribeForm>
        {Object.keys(results).length > 0 && (
          <Results data-testid="results">
            <Row>
              <Text size="20px" type="h6" color="Blue 6">
                Results
              </Text>
            </Row>
            <Row>
              {Object.keys(results).map((r, index) => (
                <Col xs={24} sm={24} md={12} lg={12} key={index}>
                  <Text size="25px" type="h5" className="result">
                    {generateResultDescription(r)}
                    {results[r]}
                  </Text>
                </Col>
              ))}
            </Row>
          </Results>
        )}
      </Calculator>
    </Container>
  );
};

export const HvacDuctForm = EmbedForm(DuctForm);
